import type { CanvasJSONLikeObject } from '@doer/frontend-components/creativity-editor';
import { ICONS, type IconName } from '../icons';
import { PILL_STYLES, type PillStyleName } from './styles';
import { buildPillChain } from './pill-chain';
import type { FontWeight } from '../text/text-defaults';

const PILL_TEXT_BASE = {
  fontFamily: 'DM Sans',
  fontStyle: 'normal',
  lineHeight: 1.16,
  charSpacing: 0,
  textAlign: 'center',
  styles: [],
  pathStartOffset: 0,
  pathSide: 'left',
  pathAlign: 'baseline',
  underline: false,
  overline: false,
  linethrough: false,
  textBackgroundColor: '',
  direction: 'ltr',
  textDecorationThickness: 66.667,
  minWidth: 20,
  splitByGrapheme: false,
  type: 'IText',
  version: '7.1.0',
  originX: 'center',
  originY: 'center',
  stroke: null,
  strokeWidth: 0,
  objectCaching: false,
  selectable: true,
  evented: true,
  top: 0
} as const;

function iconFor(name: IconName, height?: number) {
  const def = ICONS[name];
  const h = height ?? def.defaultHeight;
  return {
    src: def.src,
    side: 'right' as const,
    height: h,
    width: h * def.aspectRatio,
    gap: 8
  };
}

export interface PillPairSide {
  pieceId: string;
  text: string;
  style: PillStyleName;
  fontWeight?: FontWeight;
  icon?: IconName;
  iconHeight?: number;
}

export interface PillPairOpts {
  groupPieceId?: string;
  left: PillPairSide;
  right: PillPairSide;
  fontSize?: number;
  paddingX?: number;
  paddingY?: number;
  borderRadius?: number;
  strokeWidth?: number;
  position: { left: number; top: number; width?: number; height?: number };
}

export function buildPillPair(opts: PillPairOpts): CanvasJSONLikeObject {
  const { groupPieceId, left, right, position } = opts;
  const fontSize = opts.fontSize ?? 30;
  const paddingX = opts.paddingX ?? 20;
  const paddingY = opts.paddingY ?? 7;
  const borderRadius = opts.borderRadius ?? 26;
  const strokeWidth = opts.strokeWidth ?? 3;

  const buildSide = (side: PillPairSide, dx: number) => {
    const style = PILL_STYLES[side.style];
    return {
      ...PILL_TEXT_BASE,
      pieceId: side.pieceId,
      text: side.text,
      fontSize,
      fontWeight: side.fontWeight ?? 700,
      left: dx,
      fill: style.textFill,
      pill: {
        fill: style.fill,
        stroke: style.stroke,
        strokeWidth,
        borderRadius,
        paddingX,
        paddingY,
        minWidth: 0,
        ...(side.icon ? { icon: iconFor(side.icon, side.iconHeight) } : {})
      }
    };
  };

  return buildPillChain({
    groupPieceId,
    position: {
      left: position.left,
      top: position.top,
      width: position.width ?? 500,
      height: position.height ?? 50
    },
    leftPill: buildSide(left, -100),
    rightPill: buildSide(right, 100)
  });
}
